import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import { Loader2, Lock, AlertTriangle, Clock, ListChecks, Play, Check, MessageSquare, Send } from "lucide-react";

const PRIO = { high: "border-red-400/50 text-red-300", medium: "border-amber-400/40 text-amber-300", low: "border-white/15 text-slate-400" };
const ROLE_LABEL = (r) => (r || "").replace(/_/g, " ");

export const TaskRow = ({ t, onChanged, compact = false, showDeadline = false }) => {
    const [busy, setBusy] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [comment, setComment] = useState("");
    const set = async (patch) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/tasks/${t.id}`, patch);
            onChanged && onChanged(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
        finally { setBusy(false); }
    };
    const addComment = async () => {
        if (!comment.trim()) return;
        try {
            const { data } = await api.post(`/tasks/${t.id}/comments`, { text: comment.trim() });
            onChanged && onChanged({ ...t, comments: [...(t.comments || []), data] });
            setComment("");
            toast.success("Comment added.");
        } catch (e) { toast.error("Could not add comment."); }
    };
    return (
        <div className="border border-white/10 px-3 py-2.5" data-testid={`task-row-${t.key}-${t.campaign_id}`}>
            <div className="flex flex-wrap items-center gap-2">
                {t.status === "blocked" ? <Lock size={12} className="text-slate-500" /> :
                    t.status === "completed" ? <Check size={12} className="text-emerald-400" /> :
                        <Clock size={12} className="text-[#FF5A00]" />}
                <span className={`flex-1 min-w-[140px] text-xs ${t.status === "completed" ? "line-through text-slate-500" : "text-white"}`}>{t.title}</span>
                {!compact && t.business_name && <span className="text-[10px] font-mono-tech text-slate-500">{t.business_name}</span>}
                <span className={`px-1.5 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border ${PRIO[t.priority] || PRIO.low}`}>{t.priority}</span>
                <span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">{ROLE_LABEL(t.role)}</span>
                {showDeadline && (
                    <span className={`text-[10px] font-mono-tech ${t.escalated ? "text-red-400" : "text-slate-500"}`} data-testid={`task-deadline-${t.key}`}>{t.deadline}</span>
                )}
                <button onClick={() => setShowComments((v) => !v)} className="text-slate-500 hover:text-white" data-testid={`task-comments-toggle-${t.key}`}>
                    <MessageSquare size={12} /> {(t.comments || []).length > 0 && <span className="text-[9px]">{t.comments.length}</span>}
                </button>
                {t.status === "pending" && (
                    <button disabled={busy} onClick={() => set({ status: "in_progress" })} data-testid={`task-start-${t.key}`}
                        className="inline-flex items-center gap-1 border border-white/20 hover:border-[#FF5A00] text-white px-2 py-1 text-[9px] font-semibold uppercase tracking-widest">
                        <Play size={9} /> Start
                    </button>
                )}
                {(t.status === "pending" || t.status === "in_progress") && (
                    <button disabled={busy} onClick={() => set({ status: "completed" })} data-testid={`task-complete-${t.key}`}
                        className="inline-flex items-center gap-1 bg-[#FF5A00] hover:bg-[#E65200] text-white px-2 py-1 text-[9px] font-semibold uppercase tracking-widest">
                        <Check size={9} /> Complete
                    </button>
                )}
                {t.status === "blocked" && <span className="text-[9px] font-mono-tech uppercase tracking-widest text-slate-600">waiting on dependencies</span>}
            </div>
            {showComments && (
                <div className="mt-2 pl-5 space-y-1.5">
                    {(t.comments || []).map((c) => (
                        <div key={c.id} className="text-[11px] text-slate-400"><span className="text-white">{c.author_name}</span>: {c.text}</div>
                    ))}
                    <div className="flex gap-1.5">
                        <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment — use @email to mention"
                            data-testid={`task-comment-input-${t.key}`}
                            className="flex-1 bg-[#1B2E55] border border-white/10 text-white px-2 py-1.5 text-[11px] min-w-0 placeholder:text-slate-600" />
                        <button onClick={addComment} data-testid={`task-comment-send-${t.key}`}
                            className="bg-[#FF5A00] text-white px-2.5 text-[9px] uppercase tracking-widest"><Send size={10} /></button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const CampaignTaskList = ({ campaignId }) => {
    const [tasks, setTasks] = useState(null);
    useEffect(() => {
        api.get(`/tasks/campaign/${campaignId}`).then(({ data }) => setTasks(data.tasks)).catch(() => setTasks([]));
    }, [campaignId]);
    if (!tasks) return <Loader2 size={14} className="animate-spin text-[#FF5A00]" />;
    if (tasks.length === 0) return <div className="text-xs text-slate-500" data-testid="campaign-tasks-empty">No workflow tasks yet.</div>;
    const done = tasks.filter((t) => t.status === "completed").length;
    return (
        <div data-testid={`campaign-tasks-${campaignId}`}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">/ Workflow · {done}/{tasks.length} done</div>
            <div className="space-y-1.5">
                {tasks.map((t) => (
                    <TaskRow key={t.id} t={t} compact
                        onChanged={(nt) => setTasks((list) => {
                            const updated = list.map((x) => (x.id === nt.id ? nt : x));
                            if (nt.status === "completed") api.get(`/tasks/campaign/${campaignId}`).then(({ data }) => setTasks(data.tasks));
                            return updated;
                        })} />
                ))}
            </div>
        </div>
    );
};

const Section = ({ icon: Icon, title, items, onChanged, testid, empty }) => (
    <section className="mb-8" data-testid={testid}>
        <div className="flex items-center gap-2 mb-3">
            <Icon size={13} className="text-[#FF5A00]" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">{title}</span>
            <span className="text-[10px] font-mono-tech text-slate-600">({items.length})</span>
        </div>
        {items.length === 0 ? <div className="text-xs text-slate-600">{empty}</div> :
            <div className="space-y-1.5">{items.map((t) => <TaskRow key={t.id} t={t} onChanged={onChanged} />)}</div>}
    </section>
);

export default function TasksBoard() {
    usePageMeta({ title: "Operations board · NOXN", description: "Task workflow dashboard." });
    const [board, setBoard] = useState(null);
    const load = () => api.get("/tasks/my").then(({ data }) => setBoard(data)).catch(() => setBoard({ next_actions: [], overdue: [], blocked: [], upcoming: [], workload: {} }));
    useEffect(() => { load(); }, []);
    const onChanged = () => load();
    const totalLoad = board ? Object.values(board.workload).reduce((a, b) => a + b, 0) : 0;
    return (
        <PortalShell title="Tasks" mainClassName="p-0">
        <div className="min-h-screen bg-[#0F1E3B] text-white" data-testid="tasks-board">
            <main className="mx-auto max-w-5xl px-6 lg:px-10 py-10">
                <h1 className="font-display font-black tracking-tighter text-4xl mb-8">Your next actions.</h1>
                {!board ? <Loader2 className="animate-spin text-[#FF5A00]" size={20} /> : (
                    <>
                        <Section icon={AlertTriangle} title="Overdue" items={board.overdue} onChanged={onChanged} testid="tasks-overdue" empty="Nothing overdue — great." />
                        <Section icon={ListChecks} title="Next required actions" items={board.next_actions} onChanged={onChanged} testid="tasks-next" empty="All clear." />
                        <Section icon={Clock} title="Due soon (3 days)" items={board.upcoming} onChanged={onChanged} testid="tasks-upcoming" empty="Nothing due soon." />
                        <Section icon={Lock} title="Blocked / waiting" items={board.blocked} onChanged={onChanged} testid="tasks-blocked" empty="No blocked tasks." />
                        <section data-testid="tasks-workload">
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400 mb-3">/ Workload by role ({totalLoad} open)</div>
                            <div className="space-y-1.5">
                                {Object.entries(board.workload).sort((a, b) => b[1] - a[1]).map(([role, n]) => (
                                    <div key={role} className="flex items-center gap-3 text-xs">
                                        <span className="w-44 text-slate-400 capitalize">{ROLE_LABEL(role)}</span>
                                        <div className="flex-1 h-2 bg-white/5"><div className="h-full bg-[#FF5A00]" style={{ width: `${totalLoad ? (n / totalLoad) * 100 : 0}%` }} /></div>
                                        <span className="w-6 text-right font-mono-tech text-slate-400">{n}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </main>
        </div>
        </PortalShell>
    );
}
